/**
 * OrbitalScene — SVG-based luxury 3D "molecular / lab" scene.
 * NO botanical. Only ceramic discs, rings, spheres, bottle silhouettes.
 */
export default function OrbitalScene({ className = "" }) {
  return (
    <svg
      viewBox="0 0 600 700"
      className={className}
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <radialGradient id="ceramic" cx="35%" cy="30%" r="80%">
          <stop offset="0%" stopColor="#FDFBF7" />
          <stop offset="70%" stopColor="#E9E4DA" />
          <stop offset="100%" stopColor="#C7C1B4" />
        </radialGradient>
        <radialGradient id="forest" cx="30%" cy="30%" r="90%">
          <stop offset="0%" stopColor="#2F5647" />
          <stop offset="100%" stopColor="#12251C" />
        </radialGradient>
        <radialGradient id="glass" cx="35%" cy="30%" r="90%">
          <stop offset="0%" stopColor="rgba(253,251,247,0.95)" />
          <stop offset="100%" stopColor="rgba(215,212,207,0.3)" />
        </radialGradient>
        <filter id="soft-shadow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="14" />
        </filter>
      </defs>

      {/* Ceramic pedestal disc */}
      <ellipse cx="300" cy="620" rx="220" ry="24" fill="#000" opacity="0.14" filter="url(#soft-shadow)" />
      <ellipse cx="300" cy="600" rx="230" ry="34" fill="url(#ceramic)" />
      <ellipse cx="300" cy="596" rx="180" ry="24" fill="#FDFBF7" opacity="0.7" />

      {/* Background ring */}
      <g className="animate-spin-slow" style={{ transformOrigin: "300px 380px" }}>
        <circle cx="300" cy="380" r="220" fill="none" stroke="#D7D4CF" strokeWidth="1.2" strokeDasharray="2 4" />
      </g>
      <circle cx="300" cy="380" r="180" fill="none" stroke="#1D3A2F" strokeWidth="0.6" opacity="0.35" />

      {/* Main dark forest-green bottle */}
      <g className="animate-float-slow" style={{ transformOrigin: "300px 400px" }}>
        {/* Bottle body */}
        <rect x="245" y="270" width="110" height="290" rx="24" fill="url(#forest)" />
        <rect x="255" y="290" width="18" height="230" rx="6" fill="#FDFBF7" opacity="0.09" />
        {/* Neck */}
        <rect x="275" y="240" width="50" height="30" rx="5" fill="#1D3A2F" />
        {/* Pump */}
        <rect x="280" y="216" width="40" height="24" rx="4" fill="#2C4A3E" />
        <rect x="286" y="196" width="8" height="24" rx="3" fill="#D7D4CF" />
        <ellipse cx="290" cy="196" rx="16" ry="4" fill="#B9B2A2" />
        {/* Label — "YOUR BRAND" placeholder */}
        <rect x="255" y="355" width="90" height="90" rx="3" fill="#FDFBF7" opacity="0.96" />
        <text x="300" y="388" fontFamily="Cormorant Garamond, serif" fontSize="15" fontWeight="500" fill="#1D3A2F" textAnchor="middle" letterSpacing="1">
          YOUR
        </text>
        <text x="300" y="410" fontFamily="Cormorant Garamond, serif" fontSize="15" fontWeight="500" fill="#1D3A2F" textAnchor="middle" letterSpacing="1">
          BRAND
        </text>
        <line x1="272" y1="422" x2="328" y2="422" stroke="#556B57" strokeWidth="0.6" opacity="0.5" />
        <text x="300" y="434" fontFamily="Poppins, sans-serif" fontSize="4.5" letterSpacing="2.5" fill="#556B57" textAnchor="middle">
          BY FRESIO
        </text>
      </g>

      {/* Secondary ceramic tube — left */}
      <g className="animate-float-med" style={{ transformOrigin: "140px 480px" }}>
        <rect x="110" y="420" width="60" height="160" rx="30" fill="url(#ceramic)" />
        <rect x="110" y="560" width="60" height="20" rx="6" fill="#B9B2A2" />
        <rect x="122" y="440" width="36" height="52" rx="1.5" fill="#1D3A2F" opacity="0.9" />
        <text x="140" y="470" fontFamily="Cormorant Garamond, serif" fontSize="10" fill="#FDFBF7" textAnchor="middle">
          F.
        </text>
      </g>

      {/* Dropper right */}
      <g className="animate-float-slow" style={{ animationDelay: "1s", transformOrigin: "460px 480px" }}>
        <rect x="440" y="400" width="46" height="180" rx="10" fill="url(#glass)" stroke="#D7D4CF" strokeWidth="0.6" />
        <rect x="446" y="410" width="34" height="150" rx="4" fill="#556B57" opacity="0.75" />
        <rect x="446" y="378" width="34" height="26" rx="3" fill="#1D3A2F" />
        <rect x="454" y="330" width="18" height="52" rx="3" fill="#1D3A2F" />
        <line x1="463" y1="558" x2="463" y2="595" stroke="#1D3A2F" strokeWidth="1.2" />
        <circle cx="463" cy="600" r="4" fill="#1D3A2F" />
      </g>

      {/* Floating molecule top-left */}
      <g className="animate-float-med" style={{ animationDelay: "0.4s", transformOrigin: "120px 200px" }}>
        <line x1="90" y1="200" x2="150" y2="180" stroke="#1D3A2F" strokeWidth="1" />
        <line x1="150" y1="180" x2="145" y2="230" stroke="#1D3A2F" strokeWidth="1" />
        <line x1="90" y1="200" x2="145" y2="230" stroke="#1D3A2F" strokeWidth="1" />
        <circle cx="90" cy="200" r="10" fill="#1D3A2F" />
        <circle cx="150" cy="180" r="8" fill="#556B57" />
        <circle cx="145" cy="230" r="10" fill="#D7D4CF" stroke="#1D3A2F" strokeWidth="0.5" />
      </g>

      {/* Floating sphere top-right */}
      <g className="animate-float-slow" style={{ animationDelay: "1.4s" }}>
        <circle cx="500" cy="150" r="28" fill="url(#ceramic)" />
        <ellipse cx="493" cy="140" rx="8" ry="4" fill="#FDFBF7" opacity="0.7" />
      </g>

      {/* Small ceramic sphere bottom */}
      <g className="animate-float-med" style={{ animationDelay: "2s" }}>
        <circle cx="80" cy="590" r="16" fill="url(#ceramic)" />
      </g>
    </svg>
  );
}
